import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  WarehouseInventoriesEndpoints as Endpoints,
  type CompleteWarehouseInventoryRequest,
  type CompleteWarehouseInventoryResponse,
  type CreateWarehouseInventoryRequest,
  type CreateWarehouseInventoryResponse,
  type DeleteWarehouseInventoryRequest,
  type DeleteWarehouseInventoryResponse,
  type EditWarehouseInventoryRequest,
  type EditWarehouseInventoryResponse,
  type ExportInventoryReportToExcelRequest,
  type ExportInventoryReportToExcelResponse,
  type GetInventoriesSearchResultRequest,
  type GetInventoriesSearchResultResponse,
  type GetInventoryChangesRequest,
  type GetInventoryChangesResponse,
  type GetWarehouseInventoryByIdRequest,
  type GetWarehouseInventoryByIdResponse,
  type GetWarehouseInventoryReportRequest,
  type GetWarehouseInventoryReportResponse,
  type GetWarehouseInvetoryProductsRequest,
  type GetWarehouseInvetoryProductsResponse,
  type LevelUpInventoryReportRequest,
  type LevelUpInventoryReportResponse,
  type SearchInventoriesRequest,
  type SearchInventoriesResponse,
  type SetInventoryProductCountRequest,
  type SetInventoryProductCountResponse,
  type UpdateWarehouseInventoryProductsRequest,
  type UpdateWarehouseInventoryProductsResponse,
} from '@app/api/WarehouseInventories/types';
import { WAREHOUSES_INVENTOSIES_RESOURCE } from '@app/constants';

export const warehouseInventoriesApi = createApi({
  reducerPath: 'warehouseInventoriesApi',
  baseQuery: createApiBaseQuery(WAREHOUSES_INVENTOSIES_RESOURCE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.CreateWarehouseInventory]: mutation<
      CreateWarehouseInventoryResponse,
      CreateWarehouseInventoryRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateWarehouseInventory,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteWarehouseInventory]: mutation<
      DeleteWarehouseInventoryResponse,
      DeleteWarehouseInventoryRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteWarehouseInventory,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.EditWarehouseInventory]: mutation<
      EditWarehouseInventoryResponse,
      EditWarehouseInventoryRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditWarehouseInventory,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.GetWarehouseInventoryById]: query<
      GetWarehouseInventoryByIdResponse,
      GetWarehouseInventoryByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseInventoryById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchInventories]: mutation<
      SearchInventoriesResponse,
      SearchInventoriesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchInventories,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetInventoriesSearchResult]: query<
      GetInventoriesSearchResultResponse,
      GetInventoriesSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetInventoriesSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetWarehouseInvetoryProducts]: query<
      GetWarehouseInvetoryProductsResponse,
      GetWarehouseInvetoryProductsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseInvetoryProducts,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetInventoryChanges]: query<
      GetInventoryChangesResponse,
      GetInventoryChangesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetInventoryChanges,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetInventoryProductCount]: mutation<
      SetInventoryProductCountResponse,
      SetInventoryProductCountRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetInventoryProductCount,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.CompleteWarehouseInventory]: mutation<
      CompleteWarehouseInventoryResponse,
      CompleteWarehouseInventoryRequest
    >({
      query: ({ inventoryId, ...params }) => ({
        url: Endpoints.CompleteWarehouseInventory,
        method: Methods.POST,
        params,
        data: inventoryId,
      }),
    }),
    [Endpoints.GetWarehouseInventoryReport]: query<
      GetWarehouseInventoryReportResponse,
      GetWarehouseInventoryReportRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseInventoryReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.LevelUpInventoryReport]: mutation<
      LevelUpInventoryReportResponse,
      LevelUpInventoryReportRequest
    >({
      query: (params) => ({
        url: Endpoints.LevelUpInventoryReport,
        method: Methods.PATCH,
        params,
      }),
    }),
    [Endpoints.UpdateWarehouseInventoryProducts]: mutation<
      UpdateWarehouseInventoryProductsResponse,
      UpdateWarehouseInventoryProductsRequest
    >({
      query: ({ inventoryId, ...params }) => ({
        url: Endpoints.UpdateWarehouseInventoryProducts,
        method: Methods.POST,
        params,
        data: inventoryId,
      }),
    }),
    [Endpoints.ExportInventoryReportToExcel]: query<
      ExportInventoryReportToExcelResponse,
      ExportInventoryReportToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportInventoryReportToExcel,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useCreateWarehouseInventoryMutation,
  useDeleteWarehouseInventoryMutation,
  useEditWarehouseInventoryMutation,
  useGetWarehouseInventoryByIdQuery,
  useLazyGetWarehouseInventoryByIdQuery,
  useSearchInventoriesMutation,
  useGetInventoriesSearchResultQuery,
  useLazyGetInventoriesSearchResultQuery,
  useGetWarehouseInvetoryProductsQuery,
  useLazyGetWarehouseInvetoryProductsQuery,
  useGetInventoryChangesQuery,
  useLazyGetInventoryChangesQuery,
  useSetInventoryProductCountMutation,
  useCompleteWarehouseInventoryMutation,
  useGetWarehouseInventoryReportQuery,
  useLazyGetWarehouseInventoryReportQuery,
  useLevelUpInventoryReportMutation,
  useUpdateWarehouseInventoryProductsMutation,
  useExportInventoryReportToExcelQuery,
  useLazyExportInventoryReportToExcelQuery,
} = warehouseInventoriesApi;
